import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2, FileText, FileUp, Gavel, Undo2 } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type { Resolucion } from './contrato';

interface Documento {
  titulo: string;
  fichero: Blob | null;
}

export function ResolucionesDelArt8({
  alVerConstancia,
}: {
  alVerConstancia: (r: Resolucion) => void;
}) {
  const { puede } = useSesion();
  const clienteDeConsultas = useQueryClient();

  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [documento, setDocumento] = useState<Documento | null>(null);
  const [revirtiendo, setRevirtiendo] = useState<Resolucion | null>(null);
  // Un solo campo de fichero, oculto: el boton de la fila dice para que resolucion es y lo abre.
  const campoDelPdf = useRef<HTMLInputElement>(null);
  const paraResolucion = useRef<Resolucion | null>(null);
  const buscado = useValorDiferido(texto.trim());

  const subirPdf = useMutation({
    mutationFn: ({ r, archivo }: { r: Resolucion; archivo: File }) => {
      const formulario = new FormData();
      formulario.append('archivo', archivo);
      return api.subir<Resolucion>(`/titulos/resoluciones/${r.id}/documento`, formulario);
    },
    meta: { procesando: 'Subiendo el PDF de la resolución…' },
    onSuccess: (r) => {
      notificar.exito(r.numero, 'PDF de la resolución adjuntado');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos-resoluciones'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO SUBIR EL PDF'),
    onSettled: () => {
      paraResolucion.current = null;
    },
  });

  const listado = useQuery({
    queryKey: ['titulos-resoluciones', buscado, tipo, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Resolucion>>('/titulos/resoluciones', {
        query: { texto: buscado || undefined, tipo: tipo || undefined, pagina, tamanio },
      }),
  });

  async function verResolucion(r: Resolucion) {
    setDocumento({ titulo: `Resolución ${r.numero}`, fichero: null });
    try {
      const fichero = await api.descargar(`/titulos/resoluciones/${r.id}/resolucion.pdf`);
      setDocumento({ titulo: `Resolución ${r.numero}`, fichero });
    } catch (causa) {
      setDocumento(null);
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ABRIR EL PDF');
    }
  }

  const columnas: Columna<Resolucion>[] = [
    {
      clave: 'registrada',
      encabezado: 'Registrada',
      contenido: (r) => (
        <span className="importe block whitespace-nowrap text-[11px] text-armazon-600">
          {new Date(r.registradoEn).toLocaleDateString('es-EC', { timeZone: 'America/Guayaquil' })}
        </span>
      ),
    },
    {
      clave: 'tipo',
      encabezado: 'Tipo',
      contenido: (r) => (
        <span className="block">
          <span
            className={`inline-block rounded px-1.5 py-px text-[10px] font-bold uppercase ${
              r.tipo === 'PRESCRIPCION'
                ? 'bg-violet-50 text-violet-700'
                : 'bg-armazon-100 text-armazon-700'
            }`}
          >
            {r.tipo === 'PRESCRIPCION' ? 'Prescripción' : 'Baja'}
          </span>
          {r.motivoNombre && (
            <span className="mt-0.5 block text-[10px] uppercase text-armazon-500">
              {r.motivoNombre}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'resolucion',
      encabezado: 'Resolución',
      contenido: (r) => (
        <span className="block min-w-0">
          <span className="importe block truncate text-xs font-semibold text-armazon-800">
            {r.numero}
          </span>
          <span className="block text-[10px] uppercase text-armazon-500">
            {r.fecha.split('-').reverse().join('/')}
            {r.tramite ? ` · trámite ${r.tramite}` : ''}
          </span>
          {r.observacion && (
            <span
              title={r.observacion}
              className="block max-w-xs truncate text-[10px] uppercase text-armazon-400"
            >
              {r.observacion}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'persona',
      encabezado: 'Contribuyente',
      contenido: (r) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {r.nombre}
          </span>
          <span className="importe block text-[10px] text-armazon-500">
            {r.identificacion ?? 'Sin cédula en el SIRE'}
          </span>
        </span>
      ),
    },
    {
      clave: 'titulos',
      encabezado: 'Títulos',
      alinear: 'centro',
      contenido: (r) => <span className="importe text-xs">{r.titulos}</span>,
    },
    {
      clave: 'capital',
      encabezado: 'Capital',
      alinear: 'derecha',
      contenido: (r) => (
        <span
          className={`importe text-xs font-semibold ${r.vigente ? 'text-armazon-900' : 'text-armazon-400 line-through'}`}
        >
          ${r.capital.toFixed(2)}
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      contenido: (r) =>
        r.vigente ? (
          <span className="block">
            <span className="block text-[10px] uppercase text-armazon-500">
              Vigente · {r.registradoPor}
            </span>
            {r.pdfPendiente && (
              <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-amber-800">
                PDF pendiente de subir
              </span>
            )}
          </span>
        ) : (
          <span className="block text-[10px] uppercase text-red-700" title={r.motivoReversion}>
            Revertida · {r.revertidaPor}
          </span>
        ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (r) => (
        <span className="flex justify-end gap-1">
          {r.pdfPendiente ? (
            puede('titulos.extinguir') && (
              <BotonDeFila
                icono={FileUp}
                titulo="Subir el PDF de la resolución"
                alPulsar={() => {
                  paraResolucion.current = r;
                  campoDelPdf.current?.click();
                }}
                ocupado={subirPdf.isPending && subirPdf.variables?.r.id === r.id}
              />
            )
          ) : (
            <BotonDeFila
              icono={FileText}
              titulo="Ver el PDF de la resolución"
              alPulsar={() => void verResolucion(r)}
            />
          )}
          {r.vigente && (
            <BotonDeFila
              icono={FileCheck2}
              titulo="Constancia para el contribuyente"
              alPulsar={() => alVerConstancia(r)}
            />
          )}
          {r.vigente && puede('titulos.revertir') && (
            <BotonDeFila
              icono={Undo2}
              titulo="Revertir la resolución"
              alPulsar={() => setRevirtiendo(r)}
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Tabla
        datos={listado.data}
        cargando={listado.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => {
          setTamanio(n);
          setPagina(0);
        }}
        entidad="resoluciones"
        iconoVacio={Gavel}
        mensajeVacio="No hay prescripciones ni bajas registradas"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={texto}
              alCambiar={(v) => {
                setTexto(v);
                setPagina(0);
              }}
              marcador="Nombre, cédula o n.º de resolución…"
              className="w-72"
            />
            <select
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPagina(0);
              }}
              aria-label="Tipo"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">PRESCRIPCIONES Y BAJAS</option>
              <option value="PRESCRIPCION">SOLO PRESCRIPCIONES</option>
              <option value="BAJA">SOLO BAJAS</option>
            </select>
          </span>
        }
      />

      <input
        ref={campoDelPdf}
        type="file"
        accept="application/pdf"
        aria-label="PDF de la resolución"
        className="hidden"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          const r = paraResolucion.current;
          // Se vacia para que elegir otra vez el mismo fichero vuelva a avisar.
          e.target.value = '';
          if (archivo && r) {
            subirPdf.mutate({ r, archivo });
          }
        }}
      />

      <VistaPreviaDeImpresion
        abierto={documento !== null}
        alCerrar={() => setDocumento(null)}
        titulo={documento?.titulo ?? ''}
        subtitulo="El PDF tal como se adjuntó"
        fichero={documento?.fichero ?? null}
        cargando={documento !== null && documento.fichero === null}
        formato="hoja"
        textoDeCarga="Abriendo la resolución…"
      />

      {revirtiendo && (
        <PanelDeReversion
          resolucion={revirtiendo}
          alCerrar={() => setRevirtiendo(null)}
          alRevertir={() => {
            setRevirtiendo(null);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos-resoluciones'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos'] });
          }}
        />
      )}
    </>
  );
}

function PanelDeReversion({
  resolucion: r,
  alCerrar,
  alRevertir,
}: {
  resolucion: Resolucion;
  alCerrar: () => void;
  alRevertir: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const revertir = useMutation({
    mutationFn: () =>
      api.post<Resolucion>(`/titulos/resoluciones/${r.id}/revertir`, { motivo: motivo.trim() }),
    onSuccess: () => {
      notificar.exito(
        `${r.titulos} ${r.titulos === 1 ? 'título vuelve' : 'títulos vuelven'} a pendiente`,
        'Resolución revertida',
      );
      alRevertir();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REVERTIR'),
  });
  const listo = motivo.trim().length >= 10 && !revertir.isPending;

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      titulo="Revertir la resolución"
      descripcion={`${r.numero} · ${r.nombre}`}
      icono={Undo2}
      alEnviar={(evento) => {
        evento.preventDefault();
        if (listo) revertir.mutate();
      }}
      enviando={!listo || revertir.isPending}
    >
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-900">
        Sus {r.titulos} {r.titulos === 1 ? 'título vuelve' : 'títulos vuelven'} a deberse por $
        {r.capital.toFixed(2)}. La resolución no se borra: queda revertida, con su nombre y su
        motivo, y ya no se puede imprimir su constancia.
      </p>
      <CampoDelPanel
        etiqueta="Por qué se revierte"
        requerido
        ayuda="Por ejemplo: la resolución fue revocada por…"
      >
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          maxLength={300}
          className={`${claseEntradaPanel} h-auto py-2`}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}
